import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Quote } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TestimonialCard from './testimonials/TestimonialCard';
import TestimonialNav from './testimonials/TestimonialNav';

const HEADER_INITIAL = { opacity: 0, y: 30 };
const HEADER_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 0.6 };

const CARD_INITIAL = { opacity: 0, scale: 0.9 };
const CARD_VISIBLE = { opacity: 1, scale: 1 };
const CARD_TRANSITION = { duration: 0.6 };

const Testimonials = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const testimonials = t.testimonials.reviews;

  const next = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={HEADER_INITIAL}
          animate={isInView ? HEADER_VISIBLE : HEADER_INITIAL}
          transition={HEADER_TRANSITION}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent">
              {t.testimonials.title}
            </span>
          </h2>
        </motion.div>

        {/* Testimonial Card */}
        <motion.div
          initial={CARD_INITIAL}
          animate={isInView ? CARD_VISIBLE : CARD_INITIAL}
          transition={CARD_TRANSITION}
          className="relative"
        >
          <div className="relative p-8 md:p-12 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10">
            <Quote className="absolute top-6 right-6 w-16 h-16 text-orange-500/20" />

            <TestimonialCard
              testimonial={testimonials[currentIndex]}
              currentIndex={currentIndex}
            />

            <TestimonialNav
              total={testimonials.length}
              currentIndex={currentIndex}
              onPrev={prev}
              onNext={next}
              onSelect={setCurrentIndex}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
